import React from 'react';

import {CartItemV1, MAX_CART_ITEMS, SceneKeyParts} from './types';

export type AddItemResult = 'added' | 'already-in-cart' | 'cap-reached';

export interface CartItemPatch {
  id: string;
  patch: Partial<Omit<CartItemV1, 'id'>>;
}

/** An item and the slot it held at the moment it was removed, so an undo can put it back. */
export interface CartItemRestore {
  item: CartItemV1;
  index: number;
}

export interface CartContextValue {
  items: CartItemV1[];
  count: number;
  /** Adds an item unless its scene is already in the cart, in which case nothing changes. */
  addItem: (item: Omit<CartItemV1, 'id' | 'addedAt'>) => AddItemResult;
  removeItem: (id: string) => void;
  updateItem: (id: string, patch: Partial<Omit<CartItemV1, 'id'>>) => void;
  /**
   * Applies every patch in one write. Mutations compose within a tick either way, so this is about
   * collapsing N re-renders into one, not about correctness.
   */
  updateItems: (patches: CartItemPatch[]) => void;
  /**
   * Returns what it removed, each entry carrying the slot the item held at the moment of its own
   * removal (earlier removals in the same call already applied) — ready to hand to restoreItems.
   */
  removeItems: (ids: string[]) => CartItemRestore[];
  /**
   * Puts removed items back at the slots they held, in one write. Entries must arrive in removal
   * order, each index the item's slot when it alone was removed: the restore replays them
   * newest-first, so every splice lands on the list its index was captured against. Best effort:
   * an entry whose scene is already in the cart is skipped quietly, and once the cart is full the
   * remaining (earliest-removed) entries are dropped — the return value counts those. Each item
   * keeps its id and `addedAt`, since an undone removal never happened.
   */
  restoreItems: (entries: CartItemRestore[]) => number;
  clear: () => void;
  findBySceneKey: (parts: SceneKeyParts) => CartItemV1 | undefined;
  isFull: boolean;
  maxItems: number;
  /**
   * Identifies the user and org these items belong to, null until both resolve. Undo state holds
   * onto it to detect that the cart underneath it was swapped by an org or user switch.
   */
  scopeKey: string | null;
  /**
   * The nav badge is props-free, so an "added to cart" confirmation elsewhere in the tree needs a
   * shared handle to open its preview.
   */
  isPreviewOpen: boolean;
  setPreviewOpen: (open: boolean) => void;
}

const emptyContext: CartContextValue = {
  items: [],
  count: 0,
  addItem: () => 'cap-reached',
  removeItem: () => undefined,
  updateItem: () => undefined,
  updateItems: () => undefined,
  removeItems: () => [],
  restoreItems: () => 0,
  clear: () => undefined,
  findBySceneKey: () => undefined,
  isFull: false,
  maxItems: MAX_CART_ITEMS,
  scopeKey: null,
  isPreviewOpen: false,
  setPreviewOpen: () => undefined,
};

/**
 * Defaults to an inert cart rather than undefined: the badge and drawer render from anywhere in the
 * nav, including tests and logged-out trees that never mount the provider.
 */
export const CartContext = React.createContext<CartContextValue>(emptyContext);

export const useCart = (): CartContextValue => React.useContext(CartContext);
